import { chunk } from 'lodash';
import { MaxCandidatesConfirmedPerBatch } from '~/constants';
import { getAuthHeaders, tempMeeApi } from '~/services/api';
import { IAverageRating } from './types/averageRateTypes';
import { IBulkConfirmRequest } from './types/bulkConfirmTypes';
import { IGetJobsParams, IJobApi } from './types/jobsTypes';

async function getJobs(params: IGetJobsParams): Promise<IJobApi[]> {
  const headers = await getAuthHeaders();

  const response = await tempMeeApi.get<IJobApi[]>(`/jobs`, {
    headers: { ...headers, 'x-office': params.officeId },
    params: {
      page: params.page || 1,
      limit: params.limit || 15,
      professionalId: params.professionalId,
      status: params.status,
      officeId: params.officeId,
      sortField: params.sortField || 'job_datetime',
      sortOrder: params.sortOrder || 'asc',
    },
  });

  return response.data;
}

async function bulkConfirmJobs(request: IBulkConfirmRequest): Promise<string[]> {
  const headers = await getAuthHeaders();

  const confirmedJobIds: string[] = [];
  const failedJobIds: string[] = [];
  let firstErrorMessage = '';
  const partitions = chunk(request.payload, MaxCandidatesConfirmedPerBatch);

  for (const batch of partitions) {
    const batchPromises = batch.map(async (item) => {
      try {
        await tempMeeApi.post(
          `/jobs/${item.jobId}/confirm/${item.counterCoverId}`,
          {
            notes: item.notes,
          },
          { headers },
        );
        return { success: true, jobId: item.jobId };
      } catch (error: any) {
        return {
          success: false,
          jobId: item.jobId,
          errorMessage: error.response?.data?.message || 'An error occurred',
        };
      }
    });

    // Wait for all requests in this batch to complete
    const batchResults = await Promise.allSettled(batchPromises);

    // Process results
    batchResults.forEach((result) => {
      if (result.status === 'fulfilled') {
        const { success, jobId, errorMessage } = result.value;
        if (success) {
          confirmedJobIds.push(jobId);
        } else {
          failedJobIds.push(jobId);
          if (!firstErrorMessage) {
            firstErrorMessage = errorMessage || 'An error occurred';
          }
        }
      } else {
        if (!firstErrorMessage) {
          firstErrorMessage = 'An error occurred';
        }
      }
    });
  }

  if (request.payload.length === failedJobIds.length) {
    throw new Error(firstErrorMessage || 'All job confirmations failed');
  }

  return confirmedJobIds;
}

async function getAverageRate(officeId: string): Promise<IAverageRating> {
  const headers = await getAuthHeaders();
  const response = await tempMeeApi.get<IAverageRating>('/jobs/average-rate', {
    headers: { ...headers, 'x-office': officeId },
  });
  return response.data;
}

export const jobsApi = {
  getJobs,
  bulkConfirmJobs,
  getAverageRate,
};
